import { useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWorkOrdersOData } from '@api/enterprise/workorder/queries/useWorkOrdersOData';
import { useUserStore } from '../../../../stores/userStore';
import { useWorkOrdersStore } from '../../../../stores/workOrdersStore';
import { buildWorkOrderODataParams } from '../utils/buildWorkOrderODataParams';
import { db } from '@db/database';
import useOnlineStatus from '@hooks/useOnlineStatus';
import type {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel
} from '@mui/x-data-grid';

type UseWorkOrderDataProps = {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  searchText: string;
};

export const useWorkOrderData = ({
  paginationModel,
  sortModel,
  filterModel,
  searchText
}: UseWorkOrderDataProps) => {
  const { employeeId, employeeCompany, isAuthenticated } = useUserStore();
  const isOnline = useOnlineStatus();

  const odataParams = useMemo(() => {
    if (!isAuthenticated || !employeeId || !employeeCompany) {
      console.log('Missing auth data:', {
        isAuthenticated,
        employeeId,
        employeeCompany
      });
      return null;
    }

    const params = buildWorkOrderODataParams(
      paginationModel,
      sortModel,
      filterModel,
      searchText
    );

    console.log('Built OData params:', params);
    return params;
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    JSON.stringify(sortModel),
    JSON.stringify(filterModel),
    searchText,
    employeeId,
    employeeCompany,
    isAuthenticated
  ]);

  const onlineQuery = useWorkOrdersOData({
    queryParams: odataParams || {},
    associatedEmployeeUserName: employeeId,
    associatedEmployeeCompanyName: employeeCompany,
    enabled: isAuthenticated && !!odataParams && isOnline
  });

  const offlineQuery = useQuery({
    queryKey: ['offline', 'workorders', 'downloaded-only'],
    queryFn: async () => {
      const cachedWorkOrders = await db.cachedWorkOrders.toArray();
      const downloadedWorkOrders = [];

      for (const workOrder of cachedWorkOrders) {
        const hasDetails = await db.cachedWorkOrderDetails
          .where('jobNumber')
          .equals(String(workOrder.jobNumber))
          .limit(1)
          .count();

        if (hasDetails > 0) {
          downloadedWorkOrders.push(workOrder);
        }
      }

      return {
        value: downloadedWorkOrders,
        '@odata.count': downloadedWorkOrders.length
      };
    },
    enabled: !isOnline && isAuthenticated,
    staleTime: 0,
    gcTime: 5 * 60 * 1000
  });

  const activeQuery = isOnline ? onlineQuery : offlineQuery;
  const data = activeQuery.data;
  const rows = Array.isArray(data) ? data : data?.value || [];
  const rowCount = Array.isArray(data)
    ? data.length
    : data?.['@odata.count'] || 0;

  const { setWorkOrders, setLoading, setError } = useWorkOrdersStore();

  useEffect(() => {
    setLoading(activeQuery.isLoading);
  }, [activeQuery.isLoading, setLoading]);

  useEffect(() => {
    if (activeQuery.error) {
      setError(activeQuery.error);
    }
  }, [activeQuery.error, setError]);

  useEffect(() => {
    if (rows.length > 0) {
      setWorkOrders(rows, rowCount);
    }
  }, [rows, rowCount, setWorkOrders]);

  return {
    data,
    rows,
    rowCount,
    isLoading: isAuthenticated ? activeQuery.isLoading : false,
    error: isAuthenticated ? activeQuery.error : null,
    odataParams,
    isOffline: !isOnline,
    isFiltered: !isOnline
  };
};
